import { useEffect, useState, useRef } from 'react';
import { api } from '../api/client';
import { formatXof } from '../utils/format';

const ACCOUNT_TYPES = {
  CAISSE:       { label: 'Caisse',       color: '#3b82f6' },
  BANQUE:       { label: 'Banque',       color: '#8b5cf6' },
  MOBILE_MONEY: { label: 'Mobile Money', color: '#f59e0b' },
  AUTRE:        { label: 'Autre',        color: '#64748b' },
};

const OP_TYPES = [
  { v: 'DEPOT',     l: '+ Dépôt',     color: '#16a34a' },
  { v: 'RETRAIT',   l: '- Retrait',   color: '#dc2626' },
  { v: 'TRANSFERT', l: '⇄ Transfert', color: '#d97706' },
  { v: 'DEPENSE',   l: '- Dépense',   color: '#dc2626' },
  { v: 'SALAIRE',   l: '$ Salaire',   color: '#7c3aed' },
];

export default function Tresorerie() {
  const [tab, setTab] = useState('dashboard');
  return (
    <>
      <div className="tabs">
        <button className={`tab ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>Tableau de bord</button>
        <button className={`tab ${tab === 'journal' ? 'active' : ''}`} onClick={() => setTab('journal')}>Journal comptable</button>
        <button className={`tab ${tab === 'depenses' ? 'active' : ''}`} onClick={() => setTab('depenses')}>Dépenses</button>
        <button className={`tab ${tab === 'rappels' ? 'active' : ''}`} onClick={() => setTab('rappels')}>Rappels paiement</button>
      </div>
      {tab === 'dashboard' && <DashboardTresorerie />}
      {tab === 'journal'   && <JournalComptable />}
      {tab === 'depenses'  && <Depenses />}
      {tab === 'rappels'   && <RappelsPaiement />}
    </>
  );
}

// ── Dashboard Trésorerie ──────────────────────────────
function DashboardTresorerie() {
  const [comptes, setComptes] = useState([]);
  const [mouvements, setMouvements] = useState([]);
  const [modal, setModal] = useState(null);
  const [error, setError] = useState('');

  function load() {
    api.get('/registry/tresorerie-comptes').then((d) =>
      setComptes((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })))
    ).catch(() => {});
    api.get('/registry/tresorerie-journal').then((d) =>
      setMouvements((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })).sort((a, b) => (b.date || '').localeCompare(a.date || '')))
    ).catch(() => {});
  }

  useEffect(load, []);

  const totalSolde = comptes.reduce((s, c) => s + Number(c.solde || 0), 0);
  const now = new Date();
  const moisMouvements = mouvements.filter((m) => m.date?.slice(0, 7) === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const recettes = moisMouvements.filter((m) => ['DEPOT'].includes(m.type)).reduce((s, m) => s + Number(m.montant || 0), 0);
  const depenses = moisMouvements.filter((m) => ['DEPENSE', 'SALAIRE', 'RETRAIT'].includes(m.type)).reduce((s, m) => s + Number(m.montant || 0), 0);

  return (
    <>
      {error && <div className="form-error">{error}</div>}

      {/* Boutons d'opération */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {OP_TYPES.map((op) => (
          <button key={op.v} className="btn btn-sm" onClick={() => setModal({ type: op.v })}
            style={{ background: op.color, border: 'none' }}>
            {op.l}
          </button>
        ))}
        <button className="btn-ghost btn-sm" onClick={() => setModal({ type: 'COMPTE' })}>+ Nouveau compte</button>
      </div>

      {/* Comptes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
        {comptes.map((c) => {
          const typeInfo = ACCOUNT_TYPES[c.type] || ACCOUNT_TYPES.AUTRE;
          return (
            <div key={c.id} className="panel" style={{ borderTop: `3px solid ${typeInfo.color}` }}>
              <div className="panel-body" style={{ padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>{c.nom}</span>
                  <span className="badge" style={{ background: typeInfo.color + '20', color: typeInfo.color, fontSize: 10 }}>{typeInfo.label}</span>
                </div>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: typeInfo.color, fontFamily: 'monospace' }}>
                  {formatXof(c.solde || 0)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>SOLDE ACTUEL</div>
              </div>
            </div>
          );
        })}
        {comptes.length === 0 && (
          <div className="panel"><div className="empty">Aucun compte. Créez votre premier compte.</div></div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>
        {/* Graphe simplifié */}
        <div className="panel">
          <div className="panel-head">
            <h2>Évolution nette (30 derniers jours)</h2>
          </div>
          <div className="panel-body" style={{ padding: 16 }}>
            <SimpleLineChart data={mouvements} />
          </div>
        </div>

        {/* Mois en cours */}
        <div className="panel">
          <div className="panel-head"><h2>Mois en cours</h2></div>
          <div className="panel-body" style={{ padding: 14 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>SOLDE TOTAL</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace' }}>{formatXof(totalSolde)}</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>RECETTES</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--success)', fontFamily: 'monospace' }}>{formatXof(recettes)}</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>DÉPENSES</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--danger)', fontFamily: 'monospace' }}>{formatXof(depenses)}</div>
            </div>
            <div style={{ borderTop: '0.5px solid var(--line)', paddingTop: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>SOLDE NET</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: recettes - depenses >= 0 ? 'var(--success)' : 'var(--danger)', fontFamily: 'monospace' }}>{formatXof(recettes - depenses)}</div>
            </div>
            {/* Mini barres */}
            <div style={{ display: 'flex', gap: 12, marginTop: 14, alignItems: 'flex-end', height: 60 }}>
              {recettes > 0 && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: '100%', background: 'var(--success)', borderRadius: 4, height: Math.min(50, 50), opacity: 0.85 }} />
                  <span style={{ fontSize: 10, color: 'var(--muted)' }}>Recettes</span>
                </div>
              )}
              {depenses > 0 && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: '100%', background: 'var(--danger)', borderRadius: 4, height: Math.min(50, Math.round(depenses / Math.max(recettes, 1) * 50)), opacity: 0.85 }} />
                  <span style={{ fontSize: 10, color: 'var(--muted)' }}>Dépenses</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Derniers mouvements */}
      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-head"><h2>Derniers mouvements</h2></div>
        <div className="panel-body">
          {mouvements.length === 0 ? <div className="empty">Aucun mouvement.</div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Date</th><th>N°</th><th>Libellé</th><th>Compte</th><th>Catégorie</th><th className="num">Débit</th><th className="num">Crédit</th><th className="num">Solde après</th><th>Statut</th></tr></thead>
              <tbody>{mouvements.slice(0, 10).map((m) => {
                const isCredit = ['DEPOT'].includes(m.type);
                return (
                  <tr key={m.id}>
                    <td className="mono">{m.date ? new Date(m.date).toLocaleDateString('fr-FR') : '—'}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{m.numero || '—'}</td>
                    <td>{m.libelle || '—'}</td>
                    <td>{m.compte || '—'}</td>
                    <td>{m.categorie || '—'}</td>
                    <td className="num" style={{ color: 'var(--danger)' }}>{!isCredit ? formatXof(m.montant || 0) : '—'}</td>
                    <td className="num" style={{ color: 'var(--success)' }}>{isCredit ? formatXof(m.montant || 0) : '—'}</td>
                    <td className="num mono">{m.soldeApres ? formatXof(m.soldeApres) : '—'}</td>
                    <td><span className={`badge ${m.statut === 'Validé' ? 'green' : 'gold'}`}>{m.statut || 'Validé'}</span></td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {modal && (
        <OperationModal
          initial={modal}
          comptes={comptes}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}

function SimpleLineChart({ data }) {
  const last30 = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    last30.push(d.toISOString().slice(0, 10));
  }
  const W = 480, H = 120, PAD = 20;
  let cumul = 0;
  const points = last30.map((date) => {
    const dayMovements = data.filter((m) => m.date === date);
    dayMovements.forEach((m) => {
      if (['DEPOT'].includes(m.type)) cumul += Number(m.montant || 0);
      else cumul -= Number(m.montant || 0);
    });
    return cumul;
  });
  const min = Math.min(...points, 0);
  const max = Math.max(...points, 1);
  const range = max - min || 1;
  const xs = last30.map((_, i) => PAD + (i / 29) * (W - PAD * 2));
  const ys = points.map((v) => H - PAD - ((v - min) / range) * (H - PAD * 2));
  const path = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const area = `${path} L${xs[xs.length - 1]},${H - PAD} L${xs[0]},${H - PAD} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 120 }}>
      <defs>
        <linearGradient id="gr" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#gr)" />
      <path d={path} fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function OperationModal({ initial, comptes, onClose, onSaved }) {
  const isCompte = initial.type === 'COMPTE';
  const [f, setF] = useState({
    type: initial.type || 'DEPOT',
    date: new Date().toISOString().slice(0, 10),
    montant: '', libelle: '', compte: comptes[0]?.nom || '',
    compteDestination: '', categorie: '', reference: '',
    // Pour compte
    nom: '', typeCompte: 'CAISSE', soldeInitial: '0',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function save() {
    setSaving(true);
    try {
      if (isCompte) {
        await api.post('/registry/tresorerie-comptes', {
          data: { nom: f.nom, type: f.typeCompte, solde: Number(f.soldeInitial) },
          label: f.nom, searchText: f.nom,
        });
      } else {
        const numero = `TRN-${Date.now().toString().slice(-6)}`;
        const isCredit = f.type === 'DEPOT';
        const compteData = comptes.find((c) => c.nom === f.compte);
        const nouveauSolde = Number(compteData?.solde || 0) + (isCredit ? Number(f.montant) : -Number(f.montant));
        await api.post('/registry/tresorerie-journal', {
          data: { type: f.type, date: f.date, montant: Number(f.montant), libelle: f.libelle || f.type, compte: f.compte, compteDestination: f.compteDestination, categorie: f.categorie, reference: f.reference, numero, soldeApres: nouveauSolde, statut: 'Validé' },
          label: f.libelle || f.type, searchText: `${f.compte} ${f.libelle}`, refDate: f.date,
        });
        // Mettre à jour le solde du compte
        if (compteData) {
          await api.patch(`/registry/tresorerie-comptes/${compteData.id}`, {
            data: { ...compteData, solde: nouveauSolde }, label: compteData.nom,
          });
        }
      }
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  const categories = {
    DEPOT: ['Frais de scolarité', 'Subvention', 'Don', 'Autre'],
    RETRAIT: ['Fonctionnement', 'Maintenance', 'Autre'],
    DEPENSE: ['Fournitures', 'Salaires', 'Maintenance', 'Eau/Électricité', 'Transport', 'Autre'],
    SALAIRE: ['Salaire enseignant', 'Salaire administration', 'Primes', 'Avance'],
    TRANSFERT: ['Virement interne'],
  };

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isCompte ? 'Nouveau compte' : OP_TYPES.find((o) => o.v === f.type)?.l || 'Opération'}</h2>
        {error && <div className="form-error">{error}</div>}
        {isCompte ? (
          <>
            <div className="field"><label>Nom du compte *</label><input value={f.nom} onChange={set('nom')} /></div>
            <div className="field"><label>Type</label>
              <select value={f.typeCompte} onChange={set('typeCompte')}>
                {Object.entries(ACCOUNT_TYPES).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </div>
            <div className="field"><label>Solde initial (XOF)</label><input type="number" value={f.soldeInitial} onChange={set('soldeInitial')} /></div>
          </>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field"><label>Date *</label><input type="date" value={f.date} onChange={set('date')} /></div>
              <div className="field"><label>Montant (XOF) *</label><input type="number" min="1" value={f.montant} onChange={set('montant')} /></div>
            </div>
            <div className="field"><label>Compte {f.type === 'TRANSFERT' ? 'source' : ''} *</label>
              <select value={f.compte} onChange={set('compte')}>
                {comptes.map((c) => <option key={c.id}>{c.nom}</option>)}
              </select>
            </div>
            {f.type === 'TRANSFERT' && (
              <div className="field"><label>Compte destination *</label>
                <select value={f.compteDestination} onChange={set('compteDestination')}>
                  <option value="">—</option>
                  {comptes.filter((c) => c.nom !== f.compte).map((c) => <option key={c.id}>{c.nom}</option>)}
                </select>
              </div>
            )}
            <div className="field"><label>Catégorie</label>
              <select value={f.categorie} onChange={set('categorie')}>
                <option value="">—</option>
                {(categories[f.type] || ['Autre']).map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="field"><label>Libellé</label><input value={f.libelle} onChange={set('libelle')} placeholder="Description de l'opération" /></div>
            <div className="field"><label>Référence / Pièce justificative</label><input value={f.reference} onChange={set('reference')} /></div>
          </>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Valider'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

// ── Journal comptable ─────────────────────────────────
function JournalComptable() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ compte: '', categorie: '', type: '', du: '', au: '', validation: '' });
  const [error, setError] = useState('');

  function load() {
    api.get('/registry/tresorerie-journal').then((d) =>
      setRows((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })).sort((a, b) => (b.date || '').localeCompare(a.date || '')))
    ).catch((e) => setError(e.message));
  }
  useEffect(load, []);

  const filtered = rows.filter((r) => {
    if (filters.compte && r.compte !== filters.compte) return false;
    if (filters.type && r.type !== filters.type) return false;
    if (filters.du && r.date < filters.du) return false;
    if (filters.au && r.date > filters.au) return false;
    return true;
  });

  const totalDebit  = filtered.filter((r) => !['DEPOT'].includes(r.type)).reduce((s, r) => s + Number(r.montant || 0), 0);
  const totalCredit = filtered.filter((r) => ['DEPOT'].includes(r.type)).reduce((s, r) => s + Number(r.montant || 0), 0);

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Journal comptable</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost btn-sm">↓ Exporter Excel</button>
          <button className="btn-ghost btn-sm">↓ Export PDF</button>
        </div>
      </div>
      <div className="panel-body">
        {error && <div className="form-error">{error}</div>}

        {/* Filtres */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 14 }}>
          <div className="field"><label>Type</label>
            <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
              <option value="">Tous</option>
              {OP_TYPES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div className="field"><label>Du</label><input type="date" value={filters.du} onChange={(e) => setFilters({ ...filters, du: e.target.value })} /></div>
          <div className="field"><label>Au</label><input type="date" value={filters.au} onChange={(e) => setFilters({ ...filters, au: e.target.value })} /></div>
          <div className="field" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-sm" onClick={load} style={{ width: '100%' }}>Filtrer</button>
          </div>
        </div>

        {filtered.length === 0 ? <div className="empty">Aucun mouvement.</div> : (
          <div className="table-wrap"><table>
            <thead>
              <tr><th>Date</th><th>N°</th><th>Libellé</th><th>Compte</th><th>Catégorie</th><th className="num">Débit</th><th className="num">Crédit</th><th className="num">Solde après</th><th>Statut</th></tr>
            </thead>
            <tbody>
              {filtered.map((m) => {
                const isCredit = ['DEPOT'].includes(m.type);
                return (
                  <tr key={m.id}>
                    <td className="mono">{m.date ? new Date(m.date).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{m.numero || '—'}</td>
                    <td><b>{m.libelle || '—'}</b></td>
                    <td>{m.compte || '—'}</td>
                    <td>{m.categorie || '—'}</td>
                    <td className="num" style={{ color: 'var(--danger)', fontFamily: 'monospace' }}>{!isCredit ? formatXof(m.montant || 0) : '—'}</td>
                    <td className="num" style={{ color: 'var(--success)', fontFamily: 'monospace' }}>{isCredit ? formatXof(m.montant || 0) : '—'}</td>
                    <td className="num mono">{m.soldeApres ? formatXof(m.soldeApres) : '—'}</td>
                    <td><span className={`badge ${m.statut === 'Validé' ? 'green' : 'gold'}`}>{m.statut || 'Validé'}</span></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 600, background: 'var(--surface-soft)' }}>
                <td colSpan={5} style={{ padding: '10px 12px' }}>Total période</td>
                <td className="num" style={{ color: 'var(--danger)', fontFamily: 'monospace', padding: '10px 12px' }}>{formatXof(totalDebit)}</td>
                <td className="num" style={{ color: 'var(--success)', fontFamily: 'monospace', padding: '10px 12px' }}>{formatXof(totalCredit)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table></div>
        )}
      </div>
    </div>
  );
}

// ── Dépenses ──────────────────────────────────────────
function Depenses() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    api.get('/registry/tresorerie-journal').then((d) => {
      const all = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      setRows(all.filter((r) => ['DEPENSE', 'SALAIRE', 'RETRAIT'].includes(r.type)).sort((a, b) => (b.date || '').localeCompare(a.date || '')));
    }).catch(() => {});
  }, []);

  const total = rows.reduce((s, r) => s + Number(r.montant || 0), 0);

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Dépenses</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="badge red">{formatXof(total)} total</span>
        </div>
      </div>
      <div className="panel-body">
        {rows.length === 0 ? <div className="empty">Aucune dépense enregistrée.</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Type</th><th>Libellé</th><th>Catégorie</th><th>Compte</th><th className="num">Montant</th><th>Référence</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.date ? new Date(r.date).toLocaleDateString('fr-FR') : '—'}</td>
                <td><span className={`badge ${r.type === 'SALAIRE' ? 'navy' : 'red'}`}>{r.type}</span></td>
                <td>{r.libelle || '—'}</td>
                <td>{r.categorie || '—'}</td>
                <td>{r.compte || '—'}</td>
                <td className="num" style={{ color: 'var(--danger)', fontFamily: 'monospace', fontWeight: 700 }}>{formatXof(r.montant || 0)}</td>
                <td className="mono" style={{ fontSize: 11 }}>{r.reference || '—'}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// ── Rappels de paiement ───────────────────────────────
function RappelsPaiement() {
  const [params, setParams] = useState({ delaiJours: 14, montantMinimum: 1 });
  const [rappels, setRappels] = useState([]);
  const [eligible, setEligible] = useState(0);
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/registry/rappels-paiement-params').then((d) => {
      const records = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      if (records[0]) setParams(records[0]);
    }).catch(() => {});
    api.get('/registry/rappels-paiement').then((d) =>
      setRappels((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })).sort((a, b) => (b.date || '').localeCompare(a.date || '')))
    ).catch(() => {});
    api.get('/finance/unpaid').then((r) => setEligible((r || []).length)).catch(() => {});
  }, []);

  async function saveParams() {
    setSaved(false);
    try {
      await api.post('/registry/rappels-paiement-params', { data: params, label: 'Params rappels' });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(e.message); }
  }

  async function sendRappels() {
    setSending(true); setError('');
    try {
      const r = await api.post('/communication/send/unpaid', { channel: 'SMS', body: `Rappel : vous avez un solde impayé. Merci de régulariser votre situation auprès de l'établissement.` });
      const newRappel = { date: new Date().toISOString().slice(0, 10), nbEnvoyes: r?.sent || 0, declenchePar: 'Manuel' };
      await api.post('/registry/rappels-paiement', { data: newRappel, label: `Rappel ${newRappel.date}`, refDate: newRappel.date });
      setRappels((p) => [newRappel, ...p]);
    } catch (e) { setError(e.message); } finally { setSending(false); }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>
      <div className="panel">
        <div className="panel-head"><h2>Paramètres des rappels</h2></div>
        <div className="panel-body" style={{ padding: 16 }}>
          {error && <div className="form-error">{error}</div>}
          {saved && <div className="receipt-banner">Paramètres enregistrés ✓</div>}
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
            Année courante — Rappels automatiques à 08:00 si le module est actif.
          </div>
          <div className="grid-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="field">
              <label>Délai minimum entre deux rappels (jours) *</label>
              <input type="number" min="1" value={params.delaiJours} onChange={(e) => setParams({ ...params, delaiJours: Number(e.target.value) })} />
            </div>
            <div className="field">
              <label>Montant minimum restant (XOF) *</label>
              <input type="number" min="1" value={params.montantMinimum} onChange={(e) => setParams({ ...params, montantMinimum: Number(e.target.value) })} />
            </div>
          </div>
          <button className="btn" onClick={saveParams} style={{ width: 'auto', padding: '0 22px' }}>Enregistrer</button>
        </div>

        {/* Historique */}
        <div className="panel-head" style={{ borderTop: '0.5px solid var(--line)' }}><h2>Derniers rappels</h2></div>
        <div className="panel-body">
          {rappels.length === 0 ? <div className="empty">Aucun rappel envoyé pour l'instant.</div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Date</th><th>Envoyés</th><th>Déclenché par</th></tr></thead>
              <tbody>{rappels.slice(0, 10).map((r, i) => (
                <tr key={i}>
                  <td className="mono">{r.date ? new Date(r.date).toLocaleDateString('fr-FR') : '—'}</td>
                  <td className="num">{r.nbEnvoyes}</td>
                  <td>{r.declenchePar || '—'}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {/* Panel envoi */}
      <div className="panel">
        <div className="panel-body" style={{ padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--primary)', margin: '16px 0 4px' }}>{eligible}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>étudiant(s) éligible(s) maintenant</div>
          <button className="btn" onClick={sendRappels} disabled={sending || eligible === 0}
            style={{ width: '100%', background: eligible > 0 ? 'var(--warn)' : 'var(--muted)', border: 'none', height: 44, fontWeight: 600 }}>
            {sending ? 'Envoi…' : 'Envoyer les rappels dus'}
          </button>
        </div>
      </div>
    </div>
  );
}
